import {ReactNode, useState} from "react";
import {ThreeDots} from "@dvrt/graphql-widgets";
import BackendError from "../../BackendError";
import AddUserDialog from "./AddUserDialog";
import {useDispatch} from "react-redux";
import {showNotification} from "../../../actions";
import {defineMessage} from "react-intl";
import SharedUserManagement from "./SharedUserManagement";
import UserRow from "./UserRow";
import {HasPermission} from "../../widgets";
import FormattedButton from "../../FormattedButton";
import {useExtensionOwnerQuery, useUserLoginQuery} from "./queries.generated";
import {useDomainNameQuery} from "@/hooks";
import {ContextFilters} from "@/context/AuthProvider";

type Props = {
    domainId: number;
    extensionId: number;
};

export default ({domainId, extensionId}: Props) => {
    const userQuery = useUserLoginQuery({
        variables: {userId: extensionId},
    });

    const ownerQuery = useExtensionOwnerQuery({
        variables: {userId: extensionId},
    });

    const {domainName} = useDomainNameQuery(domainId);
    const domainContextFilter = ContextFilters.domain(domainName);

    const [isDialogOpen, setOpen] = useState(false);
    const dispatch = useDispatch();

    if (userQuery.loading) return <ThreeDots height={5} />;
    if (userQuery.error) return <BackendError error={userQuery.error} />;

    const labelExtra = (
        <HasPermission name="manage_user_logins" contextFilter={domainContextFilter}>
            <FormattedButton
                message={defineMessage({id: "userManagement.buttons.addUser", defaultMessage: "Add login"})}
                onClick={() => setOpen(true)}
            />
        </HasPermission>
    );

    const addUserDialog = (
        <AddUserDialog
            domainId={domainId}
            userId={extensionId}
            isOpen={isDialogOpen}
            close={() => setOpen(false)}
            withAliases={!userQuery.data.userLogins.length}
            onSuccess={() => addSuccess()}
        />
    );

    async function onDelete() {
        dispatch(
            showNotification(
                defineMessage({id: "userManagement.notifications.userDeleted", defaultMessage: "Login deleted"}),
                "success",
            ),
        );
        await ownerQuery.refetch();
        await userQuery.refetch();
    }

    async function addSuccess() {
        setOpen(false);
        dispatch(
            showNotification(
                defineMessage({id: "userManagement.notifications.userAdded", defaultMessage: "Login added"}),
                "success",
            ),
        );
        await userQuery.refetch();
    }

    async function onMakeOwner() {
        dispatch(
            showNotification(
                defineMessage({id: "userManagement.notifications.ownerChanged", defaultMessage: "Owner changed"}),
                "success",
            ),
        );
        await ownerQuery.refetch();
        await userQuery.refetch();
    }

    function createRows(): ReactNode[] {
        const rows = [];
        let i = 0;
        for (const user of userQuery.data.userLogins) {
            rows.push(
                <UserRow
                    key={i}
                    user={user}
                    actionsPermission={{name: "manage_user_logins", contextFilter: domainContextFilter}}
                    onDelete={() => onDelete()}
                    onMakeOwner={() => onMakeOwner()}
                    extensionId={extensionId}
                    changeOwnerDisabled={user.userSub === ownerQuery.data.userOwner.userSub}
                />,
            );
            i++;
        }
        return rows;
    }

    return (
        <SharedUserManagement
            label={defineMessage({id: "userManagement.title", defaultMessage: "Assigned logins"})}
            labelExtra={labelExtra}
            addUserDialog={addUserDialog}
            createRows={createRows}
            domainName={domainName}
        />
    );
};
